<script setup>
import { computed, onBeforeMount, onMounted, ref, useTemplateRef, watch } from 'vue';
import InactiveJobCupcake from 'module/inactiveJobCupcake/InactiveJobCupcake.vue';
import KeyfactBox from 'module/keyfactBox/KeyfactBox.vue';
import ProduktBuildingFirmenalarmSpot from 'module/spot/files/spot_produkt-building-firmenalarm.svg?component';
import MemoComponent from 'module/memo/MemoComponent.vue';
import SuggestionList from 'module/suggestionList/SuggestionList.vue';
import { useEventListener, useParentElement } from '@vueuse/core';
import { trackGa4 } from 'util/googleAnalytics';
import { log } from '@karriereat/action-logger';
import { CONSENT_GROUPS, hasConsent, onConsentChange } from 'util/consent';
import { postJson } from 'util/xhr';
import { user } from 'app/user';
import { API_ENDPOINT_LOG_APPLICATION_CLICK } from 'app/endpoints';
import { isSSR } from 'util/ssr';
import { dangerouslySetInnerHtml } from 'dangerously-set-html-with-scripts';

const props = defineProps({
    jobDetail: {
        type: Object,
    },
    jobContent: {
        type: Object,
    },
    participatingCompanyData: {
        type: Object,
    },
    isSplitView: {
        type: Boolean,
    },
    client: {
        type: Boolean,
    },
    tabs: {
        type: Array,
    },
    keyfactBox: {
        type: Object,
    },
    jobSuggestions: {
        type: Object,
    },
    isSeamless: {
        type: Boolean,
    },
    jobBundleLabel: {
        type: String,
    },
});
const emit = defineEmits(['removeModalStyle', 'addModalStyle', 'openDeleteModal', 'showSnackbar',
    'memoSaved', 'memoDeleted', 'showMemoOverlay', 'hideMemoOverlay', 'jobContentLoaded', 'jobContentLoading']);

const showIframeContainerJobStandalone = computed(() => !props.isSplitView);
const showJobTextStandalone = computed(() => !props.isSplitView);
const activeTab = computed(() => props.tabs.find((tab) => tab.active));

function logApplicationClick(data) {
    postJson(
        `${API_ENDPOINT_LOG_APPLICATION_CLICK}`,
        { data },
    );
}

function onSendMailToEvent() {
    trackGa4({
        event: 'click_element',
        element: 'JOBD: External Link',
        element_detail: 'Mailto',
    });
}

function onSendOtherEvent(event) {
    trackGa4({
        event: 'click_element',
        element: 'JOBD: External Link',
        element_detail: event.target.innerHTML,
    });
}

function onSendApplyLinkEvent(method) {
    trackGa4({
        event: 'job_apply_click',
        application_type: 'SmartBewerben',
        job_id: props.jobContent.id ?? null,
        job_type: props.isSeamless ? 'SJA' : 'Standard',
        product_type: props.jobBundleLabel,
        method,
        user_data: {
            email: user.getUserMail(),
        },
    });

    const data = {
        jobId: props.jobContent.id ?? null,
        jobBundleId: props.jobContent.jobBundleId,
        type: 'smartbewerben',
    };

    log('apply-click', data);
    logApplicationClick(data);
}

function addLinkEvents(contentDocument) {
    const links = contentDocument.querySelectorAll('a');

    links.forEach((link) => {
        if (link.hasAttribute('data-is-smart-apply-link')
            && link.getAttribute('data-is-smart-apply-link') === 'true') {
            link.addEventListener('click', () => {
                onSendApplyLinkEvent('Inline Apply');
            });
        } else if (link.href.startsWith('mailto')) {
            link.addEventListener('click', onSendMailToEvent);
        } else {
            link.addEventListener('click', onSendOtherEvent);
        }
    });
}

/* iFrame functionality */
/*
    TODO
    this section is all kinds of messed up since it neither works with Vue reactivity in mind, nor should it exist
    much longer since seamless job ads should come soon(tm)

    UPDATE TODO - even with seamless job ads the iframe is here to stay for a while longer so this should probably
    be refactored to work nicer with vue if we decide to proceed with the k4 FE transformation (ie. it proves to be
    reliable on production)
 */

const iFrameRef = ref(null);
const iFrameLoaded = ref(false);
watch(activeTab, () => {
    iFrameLoaded.value = false;
});

function updateIFrameHeight() {
    const { value: iFrameContainer } = useParentElement(iFrameRef);

    // FF / Safari height fix: if iframe is not ready yet, do nothing
    // Manual iFrameLoaded logic because safari reports readyState as complete even though it should be "uninitialized".
    if (!iFrameLoaded.value || iFrameRef.value.contentDocument.readyState !== 'complete') return;

    let scrollPosition;
    if (props.isSplitView) {
        scrollPosition = document?.getElementById('job-detail-container').scrollTop;
    } else {
        scrollPosition = window.scrollY;
    }

    iFrameRef.value.style.height = 'auto';
    iFrameContainer.style.height = 'auto';

    const contentHeight = iFrameRef.value?.contentDocument?.documentElement?.scrollHeight;

    if (contentHeight) {
        iFrameRef.value.style.height = `${contentHeight}px`;
        iFrameContainer.style.height = `${contentHeight}px`;
    }

    // Restore scroll position to avoid scroll jumping
    if (props.isSplitView) {
        document.getElementById('job-detail-container').scrollTop = scrollPosition;
    } else {
        window.scroll(0, scrollPosition);
    }
}

function initializeSeamlessJobVideo() {
    if (!props.isSeamless || isSSR()) return;

    if (hasConsent(CONSENT_GROUPS.performance)) {
        // SjaTemplate namespace exposed by SJA template renderer
        window.SjaTemplate?.loadVideo();
    }
}

async function iFrameUpdate() {
    try {
        const contentDocument = (iFrameRef.value.contentWindow || iFrameRef.value.contentDocument).document;
        updateIFrameHeight();
        addLinkEvents(contentDocument);
    } catch (e) {
        // 'Cannot access iframe'
    }

    iFrameRef.value?.classList.remove('m-jobContent__iFrame--loading');
    emit('jobContentLoaded');
}

const jobElement = ref(null);
function onIFrameLoad() {
    iFrameLoaded.value = true;

    iFrameUpdate();

    jobElement.value = iFrameRef.value.contentDocument.querySelector('.job');
}

onBeforeMount(() => emit('jobContentLoading'));
onMounted(() => onConsentChange(initializeSeamlessJobVideo));

const seamlessHtmlRef = useTemplateRef('seamlessHtmlRef');
onMounted(() => {
    if (props.isSeamless) {
        dangerouslySetInnerHtml(seamlessHtmlRef.value, props.jobContent.html);

        if (hasConsent(CONSENT_GROUPS.performance)) {
            // SjaTemplate namespace exposed by SJA template renderer
            window.SjaTemplate?.loadVideo();
        }
    }
});

let resizeTimeout;
useEventListener('resize', () => {
    if (resizeTimeout) {
        cancelAnimationFrame(resizeTimeout);
    }
    resizeTimeout = requestAnimationFrame(iFrameUpdate);
});

// eslint-disable-next-line max-len
const companyIFrameSrc = computed(() => `/x/${props.jobDetail.companySlug}?isSplitView=${props.isSplitView}${props.jobDetail.noCache ? '&noCache=1' : ''}`);

// TODO make Vue when used with controllers that use this
function allowPointerEvents() {
    if (iFrameRef.value) {
        iFrameRef.value.classList.remove('m-jobContent__iFrame-noPointerEvents');
    }
}

/* Memo functionality */
// TODO think about how this could be better, since drilling / bubbling this up and down is kinda bad
const memoComponent = ref(null);

function deleteMemo() {
    memoComponent.value?.deleteMemo();
}

function saveMemo() {
    memoComponent.value?.saveMemo();
}

function memoContentSimilar() {
    return memoComponent.value?.memoContentSimilar() ?? false;
}

function getTextareaValue() {
    return memoComponent.value?.getTextareaValue() ?? false;
}

defineExpose({ allowPointerEvents, deleteMemo, saveMemo, memoContentSimilar, getTextareaValue });
</script>

<template>
    <div
        :data-bundle-id="jobDetail.jobBundleId"
        :data-chiffre="jobDetail.isChiffreJob"
        :data-company-name="jobDetail.companyName"
        :data-company-slug="jobDetail.companySlug"
        :data-id="jobDetail.id"
        :data-is-smart-apply-job="jobContent?.isSmartApplyJob"
        :data-roledescription="participatingCompanyData?.roleDescription"
        :data-splitview="isSplitView"
        :data-src-doc="jobContent?.html"
        :data-watchlist-id="jobContent?.watchlistId"
        class="m-jobContent"
    >
        <div
            class="m-jobContent__jobDetail"
            :class="{
                'm-jobContent__jobDetail--seamless': isSeamless,
                'm-jobContent__jobDetail--splitview': isSeamless && isSplitView,
            }"
        >
            <template v-if="activeTab.id === 'job'">
                <div
                    v-if="jobDetail.isInactive"
                    class="m-jobContent__inactiveJobCupcake"
                >
                    <InactiveJobCupcake :cupcake-text="jobContent?.inactiveJobCupcake.cupcakeText"/>
                </div>
                <div
                    v-else
                    class="m-jobContent__rows"
                >
                    <div
                        class="m-jobContent__jobContainer"
                        :class="{
                            'm-jobContent__jobContainer--html': isSeamless,
                            'm-jobContent__jobContainer--iframe': !isSeamless,
                            'm-jobContent__jobContainer--standalone': showIframeContainerJobStandalone,
                        }"
                    >
                        <div
                            v-if="isSeamless"
                            ref="seamlessHtmlRef"
                            v-html="jobContent.html"
                        />
                        <iframe
                            v-else
                            ref="iFrameRef"
                            :srcdoc="jobContent?.html"
                            class="m-jobContent__iFrame m-jobContent__iFrame--loading m-jobContent__iFrame--job"
                            frameborder="0"
                            scrolling="no"
                            title="Informationen zum Stelleninserat"
                            @load="onIFrameLoad"
                        />
                    </div>

                    <div
                        class="m-jobContent__jobText"
                        :class="{
                            'm-jobContent__jobText--standalone': showJobTextStandalone,
                            'm-jobContent__jobText--seamless': isSeamless,
                        }"
                        v-html="jobContent?.text"
                    />

                    <div
                        v-if="!isSeamless"
                        class="m-jobContent__side"
                    >
                        <KeyfactBox
                            :company="keyfactBox.company"
                            :enabled="keyfactBox.enabled"
                            :job="keyfactBox.job"
                        />
                    </div>
                </div>
            </template>
            <template v-else-if="activeTab.id === 'company'">
                <div
                    v-if="jobDetail.isChiffreJob"
                    class="m-jobContent__chiffre"
                >
                    <ProduktBuildingFirmenalarmSpot class="m-jobContent__chiffreSpot"/>
                    <p class="m-jobContent__chiffreText">
                        <span>Der Arbeitgeber möchte vorerst keine Informationen bekanntgeben.</span>
                        <span>Details zum Unternehmen erfährst du im Laufe des Bewerbungsprozesses.</span>
                    </p>
                </div>
                <div
                    v-else
                    :data-company-slug="jobDetail.companySlug"
                    class="m-jobContent__companyContainer"
                >
                    <iframe
                        ref="iFrameRef"
                        :src="companyIFrameSrc"
                        class="m-jobContent__iFrame m-jobContent__iFrame--loading"
                        title="Informationen zum Arbeitgeber"
                        @load="onIFrameLoad"
                    />
                </div>
            </template>
            <template v-else-if="activeTab.memoTab">
                <div class="m-jobContent__memoContainer">
                    <div class="m-jobContent__memoContainerInner">
                        <div class="m-jobContent__infoBox k-c-infoBox k-c-infoBox--icon k-c-infoBox--hint">
                            <div class="k-c-infoBox__content">
                                <div class="m-jobContent__infoBoxContent">
                                    <div class="m-jobContent__infoBoxContentInner">
                                        Hast du Gedanken und Fragen zum Job? Notiere dir die Infos, die du nicht
                                        vergessen möchtest.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="m-jobContent__memo">
                            <MemoComponent ref="memoComponent"/>
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <template
            v-for="(tab, index) in tabs"
            :key="index"
        >
            <template v-if="tab.job">
                <div
                    v-if="jobSuggestions?.active"
                    class="m-jobContent__relatedWrapper"
                >
                    <div class="m-jobContent__suggestions">
                        <SuggestionList
                            :has-more-jobs="jobSuggestions?.hasMoreJobs"
                            :headline="jobSuggestions?.headline"
                            :is-landing-page="jobSuggestions?.isLandingPage"
                            :job-id="jobSuggestions?.jobId"
                            :lazy="jobSuggestions?.lazy"
                            :link="jobSuggestions?.link"
                            :link-text="jobSuggestions?.linkText"
                            :recent-jobs="jobSuggestions?.recentJobs"
                            :items="jobSuggestions?.items"
                        />
                    </div>
                    <div class="m-jobContent__lastViewed">
                        <SuggestionList
                            :job-id="jobContent?.id"
                            headline="Diese Jobs hast du dir zuletzt angesehen"
                            hidden
                            lazy
                            recent-jobs
                        />
                    </div>
                </div>
            </template>
        </template>
    </div>
</template>
<style lang="scss">
@use "styles";
</style>
